import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Send, User } from 'lucide-react';
import { toast } from 'sonner';
import { getActiveTimezone } from '@/utils/timezone';
import {
  supportTicketApi,
  SUPPORT_TICKET_MAX_IMAGES,
  SUPPORT_TICKET_VIDEO_MAX_MB,
} from '@/services/supportTicketApi';
import { MultiImageUpload } from '@/components/common/MultiImageUpload';
import TicketAttachmentGallery from '@/components/support/TicketAttachmentGallery';
import TicketVideoPicker from '@/components/support/TicketVideoPicker';

const STATUS_COLORS: Record<string, string> = {
  OPEN: 'bg-red-100 text-red-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  AWAITING_REPLY: 'bg-amber-100 text-amber-700',
  RESOLVED: 'bg-green-100 text-green-700',
  CLOSED: 'bg-gray-100 text-gray-600',
};

const MESSAGE_MAX = 2000;

export default function SupportTicketDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  // Files attached to the reply being composed.
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [video, setVideo] = useState<File | null>(null);

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['support-ticket', id],
    queryFn: () => supportTicketApi.getById(id!),
    enabled: !!id,
  });

  const messageMutation = useMutation({
    mutationFn: (msg: string) => supportTicketApi.addMessage(id!, { message: msg }, { images, video }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket', id] });
      setMessage('');
      setImages([]);
      setPreviews([]);
      setVideo(null);
      toast.success('Message sent');
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Failed to send message'),
  });

  const handleSendMessage = () => {
    const msg = message.trim();
    if (!msg) return toast.error('Message is required');
    if (msg.length > MESSAGE_MAX) return toast.error(`Message must be under ${MESSAGE_MAX} characters`);
    messageMutation.mutate(msg);
  };

  const closeMutation = useMutation({
    mutationFn: () => supportTicketApi.close(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['my-support-tickets'] });
      toast.success('Ticket closed');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (!ticket) {
    return <div className="text-center py-12 text-gray-400">Ticket not found</div>;
  }

  return (
    <div>
      <button
        onClick={() => navigate('/support-tickets')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft className="h-4 w-4" /> Back to tickets
      </button>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
          <div className="min-w-0">
            <span className="text-xs font-mono text-indigo-600">{ticket.ticketNo}</span>
            <h1 className="text-lg sm:text-2xl font-bold tracking-tight text-gray-900 m-0">{ticket.subject}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {new Date(ticket.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: getActiveTimezone() })}
              {' · '}{ticket.category}
            </p>
          </div>
          <div className="flex items-center gap-2 sm:justify-end w-full sm:w-auto shrink-0">
            <span className={`px-2 py-1 text-xs rounded-full font-medium ${STATUS_COLORS[ticket.status]}`}>
              {ticket.status.replace(/_/g, ' ')}
            </span>
            {ticket.status !== 'CLOSED' && ticket.status !== 'RESOLVED' && (
              <button onClick={() => closeMutation.mutate()} className="text-xs text-gray-500 hover:text-red-600 font-medium">
                Close Ticket
              </button>
            )}
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-700 whitespace-pre-wrap">{ticket.description}</div>
        <TicketAttachmentGallery urls={ticket.screenshots} label="Screenshot" className="mt-4" />
        {ticket.resolution && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs font-semibold text-green-700 mb-1">Resolution</p>
            <p className="text-sm text-green-800">{ticket.resolution}</p>
          </div>
        )}
      </div>

      <div className="space-y-3 mb-4">
        {ticket.messages?.map((msg: any) => (
          <div
            key={msg.id}
            className={`rounded-xl p-4 ${msg.isStaffReply ? 'bg-indigo-50 border border-indigo-200 ml-6' : 'bg-white border border-gray-200 mr-6'}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs text-white ${msg.isStaffReply ? 'bg-indigo-500' : 'bg-gray-400'}`}>
                <User className="h-3 w-3" />
              </div>
              <span className="text-sm font-medium text-gray-900">{msg.user?.name}</span>
              {msg.isStaffReply && (
                <span className="text-[10px] px-1.5 py-0.5 bg-indigo-200 text-indigo-700 rounded font-medium">Support Team</span>
              )}
              <span className="text-xs text-gray-400 ml-auto">
                {new Date(msg.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: getActiveTimezone() })}
              </span>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{msg.message}</p>
            <TicketAttachmentGallery urls={msg.attachments} label="Attachment" className="mt-3" />
          </div>
        ))}
      </div>

      {ticket.status !== 'CLOSED' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <textarea
            rows={3}
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
          />
          <div className="mt-2">
            <MultiImageUpload
              images={images}
              previews={previews}
              onChange={(files, urls) => { setImages(files); setPreviews(urls); }}
              maxImages={SUPPORT_TICKET_MAX_IMAGES}
              accept="image/jpeg,image/png,image/webp"
            />
            <TicketVideoPicker video={video} onChange={setVideo} className="mt-2" />
          </div>
          <div className="flex items-center justify-between gap-3 mt-2">
            <span className="text-[11px] text-gray-400">
              Attach up to {SUPPORT_TICKET_MAX_IMAGES} images (JPG, PNG, WebP) and 1 video (max {SUPPORT_TICKET_VIDEO_MAX_MB} MB)
            </span>
            <button
              onClick={handleSendMessage}
              disabled={!message.trim() || messageMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {messageMutation.isPending ? (video ? 'Uploading video...' : 'Sending...') : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
